import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import type { TelegramAuthData } from '@/types';
import LoadingSpinner from './LoadingSpinner';

interface TelegramLoginProps {
  botUsername: string;
  onSuccess?: () => void;
  onNewUser?: (registrationToken: string, telegramData: TelegramAuthData) => void;
}

declare global {
  interface Window {
    TelegramLoginWidget: {
      dataOnauth: (user: TelegramAuthData) => void;
    };
  }
}

export default function TelegramLogin({ botUsername, onSuccess, onNewUser }: TelegramLoginProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { setAuth, setError } = useAuthStore();
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);

  useEffect(() => {
    // Define the callback function
    window.TelegramLoginWidget = {
      dataOnauth: handleTelegramAuth,
    };

    // Create and inject the Telegram widget script
    if (containerRef.current) {
      containerRef.current.innerHTML = '';

      const script = document.createElement('script');
      script.src = 'https://telegram.org/js/telegram-widget.js?22';
      script.setAttribute('data-telegram-login', botUsername);
      script.setAttribute('data-size', 'large');
      script.setAttribute('data-radius', '8');
      script.setAttribute('data-onauth', 'TelegramLoginWidget.dataOnauth(user)');
      script.setAttribute('data-request-access', 'write');
      script.async = true;

      containerRef.current.appendChild(script);
    }

    return () => {
      // Cleanup
      delete (window as { TelegramLoginWidget?: unknown }).TelegramLoginWidget;
    };
  }, [botUsername]);

  const handleTelegramAuth = async (telegramData: TelegramAuthData) => {
    setIsVerifying(true);
    setVerificationError(null);

    try {
      const result = await api.verifyTelegramAuth(telegramData);

      if (result.authenticated && result.sessionToken && result.user) {
        // Existing user - logged in
        setAuth(result.user, result.sessionToken);
        onSuccess?.();
      } else if (result.isNewUser && result.registrationToken) {
        // New user - needs registration
        onNewUser?.(result.registrationToken, telegramData);
      } else if (result.conflict) {
        setVerificationError(
          `This Telegram account is already linked to another user${
            result.existingUsername ? ` (@${result.existingUsername})` : ''
          }. Please use a different Telegram account.`
        );
      }
    } catch (error) {
      console.error('Telegram verification failed:', error);
      const message = error instanceof Error ? error.message : 'Verification failed';
      setVerificationError(message);
      setError(message);
    } finally {
      setIsVerifying(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="flex flex-col items-center space-y-4">
        <LoadingSpinner size="lg" />
        <p className="text-dark-300">Verifying Telegram account...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {verificationError && (
        <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200 text-sm">
          {verificationError}
        </div>
      )}

      <div className="flex flex-col items-center space-y-4">
        <div ref={containerRef} className="telegram-login-container" />
        <p className="text-xs text-dark-400 text-center">
          Login with your Telegram account to access your trading data
        </p>
      </div>
    </div>
  );
}
